import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { RootState } from "..";
import { DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS } from "@models/Dataset";
import type { GuardrailRollingOption, PointInTimeViewerOptions } from "@models/Dataset";
import type {
  ScorecardBrand,
  ScorecardBrandCandidate,
  ScorecardConfig,
  ScorecardDatasetMetricRow,
  ScorecardHeadingRow,
  ScorecardQuestionRow,
  ScorecardRow,
  ScorecardSourceQuestion,
  ScorecardTimePeriod,
  ScorecardWeightSelection,
} from "@models/Scorecard";
import { normalizeScorecardRow } from "@models/Scorecard";
import {
  cloneQuestionRowForDuplicate,
  findDependentRows,
  flagNewBrandOnRows,
  stripBrandFromRows,
} from "@utils/scorecard-row-lifecycle-utils";
import { getDefaultBrandOrder, getOrderedBrands } from "@utils/scorecard-column-utils";

export interface ScorecardConfigState {
  currentConfig: ScorecardConfig;
  savedConfigs: ScorecardConfig[];
  datasetId: string | null;
  datasetName: string | null;
  datasetVersionId: number | null;
}

const defaultScorecardConfig: ScorecardConfig = {
  title: "Scorecard",
  subtitle: "",
  sourceQuestion: null,
  brandCandidates: [],
  brands: [],
  columnOrder: [],
  hiddenBrandIds: [],
  metricColumnHeader: "Metric",
  primaryBrandHighlightColor: null,
  rows: [],
  weight: "DISABLED",
  percentageFormatting: 0,
  meanFormatting: 0,
  timePeriodSelection: {
    timePeriod: "all_time",
    startDate: null,
    endDate: null,
  },
  rollingAverage: {
    enabled: false,
    windowSize: 2,
  },
  viewerOptions: DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
};

export const initialState: ScorecardConfigState = {
  currentConfig: { ...defaultScorecardConfig },
  savedConfigs: [],
  datasetId: null,
  datasetName: null,
  datasetVersionId: null,
};

/** Primary first, then competitors in source-question order. */
function reorderBrands(brands: ScorecardBrand[]): ScorecardBrand[] {
  const primary = brands.find((b) => b.role === "primary");
  const competitors = brands
    .filter((b) => b.role === "competitor")
    .sort((a, b) => a.sourceOrder - b.sourceOrder);
  return primary ? [primary, ...competitors] : competitors;
}

export const scorecardConfigSlice = createSlice({
  name: "scorecardConfig",
  initialState,
  reducers: {
    updateChartMetadata: (
      state,
      action: PayloadAction<{
        datasetId: string;
        datasetName?: string;
        datasetVersionId?: number | null;
      }>,
    ) => {
      state.datasetId = action.payload.datasetId;
      state.datasetName = action.payload.datasetName ?? null;
      state.datasetVersionId = action.payload.datasetVersionId ?? null;
    },
    updateChartTitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.title = action.payload;
    },
    updateChartSubtitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.subtitle = action.payload;
    },
    updateWeight: (state, action: PayloadAction<ScorecardWeightSelection>) => {
      state.currentConfig.weight = action.payload;
    },
    updatePercentageFormatting: (state, action: PayloadAction<0 | 1 | 2>) => {
      state.currentConfig.percentageFormatting = action.payload;
    },
    updateMeanFormatting: (state, action: PayloadAction<0 | 1 | 2>) => {
      state.currentConfig.meanFormatting = action.payload;
    },
    updateTimePeriod: (state, action: PayloadAction<ScorecardTimePeriod>) => {
      state.currentConfig.timePeriodSelection.timePeriod = action.payload;
      if (action.payload !== "custom_period") {
        state.currentConfig.timePeriodSelection.startDate = null;
        state.currentConfig.timePeriodSelection.endDate = null;
      }
    },
    updateTimePeriodDates: (
      state,
      action: PayloadAction<{ startDate: string | null; endDate: string | null }>,
    ) => {
      state.currentConfig.timePeriodSelection.startDate = action.payload.startDate;
      state.currentConfig.timePeriodSelection.endDate = action.payload.endDate;
    },
    updateRollingAverageEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.rollingAverage.enabled = action.payload;
      if (!action.payload) {
        state.currentConfig.rollingAverage.windowSize = 2;
      }
    },
    updateRollingAverageWindowSize: (
      state,
      action: PayloadAction<GuardrailRollingOption>,
    ) => {
      state.currentConfig.rollingAverage.windowSize = action.payload;
    },
    updateViewerOptions: (state, action: PayloadAction<PointInTimeViewerOptions>) => {
      state.currentConfig.viewerOptions = action.payload;
    },
    /** Sets the Source Brand Question and its brand candidates/selection in one
     *  shot — used once, right before the Scorecard editor opens on Create. */
    setSourceBrandQuestion: (
      state,
      action: PayloadAction<{
        sourceQuestion: ScorecardSourceQuestion;
        brandCandidates: ScorecardBrandCandidate[];
        brands: ScorecardBrand[];
      }>,
    ) => {
      state.currentConfig.sourceQuestion = action.payload.sourceQuestion;
      state.currentConfig.brandCandidates = action.payload.brandCandidates;
      state.currentConfig.brands = reorderBrands(action.payload.brands);
      // Only set the default chart title if it's still the untouched default
      // "Scorecard" — mirrors chart-config-slice's updateQuestions, which
      // does the same for a new Bar chart's question, while preserving a
      // researcher-edited title when the Source Brand Question is changed later.
      if (state.currentConfig.title === defaultScorecardConfig.title) {
        state.currentConfig.title = action.payload.sourceQuestion.questionTitle;
      }
    },
    /** Promotes a candidate to Primary; the prior Primary (if any) returns to
     *  its source-order position among the Competitors. */
    setPrimaryBrand: (state, action: PayloadAction<string>) => {
      const sourceId = action.payload;
      const brands = state.currentConfig.brands.map((b) => ({ ...b }));
      const candidate = brands.find((b) => b.sourceId === sourceId);
      if (!candidate) return;
      brands.forEach((b) => {
        if (b.sourceId === sourceId) b.role = "primary";
        else if (b.role === "primary") b.role = "competitor";
      });
      state.currentConfig.brands = reorderBrands(brands);
    },
    /** Replaces the full Competitor set in one shot — matches the multi-select
     *  dropdown's contract (it always reports the complete desired selection,
     *  not a single toggle). Reconciles added/removed brands against every
     *  existing row exactly like adding/removing one at a time would. Never
     *  touches Primary; no-ops if the result would leave zero Competitors. */
    setCompetitorBrandSourceIds: (state, action: PayloadAction<string[]>) => {
      const { brands, brandCandidates } = state.currentConfig;
      const primary = brands.find((b) => b.role === "primary");
      const desiredIds = new Set(
        action.payload.filter((id) => id !== primary?.sourceId),
      );
      if (desiredIds.size === 0) return;

      const currentCompetitors = brands.filter((b) => b.role === "competitor");
      const currentIds = new Set(currentCompetitors.map((b) => b.sourceId));

      const kept = currentCompetitors.filter((b) => desiredIds.has(b.sourceId));
      const removed = currentCompetitors.filter((b) => !desiredIds.has(b.sourceId));

      const added: ScorecardBrand[] = Array.from(desiredIds)
        .filter((id) => !currentIds.has(id))
        .map((id) => brandCandidates.find((c) => c.sourceId === id))
        .filter((c): c is ScorecardBrandCandidate => !!c)
        .map((c) => ({
          id: crypto.randomUUID(),
          role: "competitor",
          sourceId: c.sourceId,
          label: c.label,
          sourceOrder: c.sourceOrder,
        }));

      state.currentConfig.brands = reorderBrands([
        ...(primary ? [primary] : []),
        ...kept,
        ...added,
      ]);

      let rows = state.currentConfig.rows;
      removed.forEach((brand) => {
        rows = stripBrandFromRows(rows, brand.id);
      });
      added.forEach((brand) => {
        rows = flagNewBrandOnRows(rows, brand);
      });
      state.currentConfig.rows = rows;
    },
    /** Moves any brand column — Primary included — within the display
     *  order. Operates on the reconciled order (see getOrderedBrands) so a
     *  stale/missing columnOrder self-heals into a complete, correct one on
     *  first drag. */
    reorderBrandColumns: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      const { fromIndex, toIndex } = action.payload;
      const order = getOrderedBrands(
        state.currentConfig.brands,
        state.currentConfig.columnOrder,
      ).map((b) => b.id);
      if (
        fromIndex < 0 ||
        fromIndex >= order.length ||
        toIndex < 0 ||
        toIndex >= order.length
      ) {
        return;
      }
      const [moved] = order.splice(fromIndex, 1);
      order.splice(toIndex, 0, moved);
      state.currentConfig.columnOrder = order;
    },
    /** Restores the default display order (Primary first, then Competitors
     *  in source order) without touching visibility or any other setting. */
    resetBrandColumnOrder: (state) => {
      state.currentConfig.columnOrder = getDefaultBrandOrder(
        state.currentConfig.brands,
      ).map((b) => b.id);
    },
    /** Toggles any brand — Primary included. A no-op if this brand is
     *  currently the only visible one, so at least one column always stays
     *  visible (the UI also disables the control in that case). */
    toggleBrandColumnVisibility: (state, action: PayloadAction<string>) => {
      const { brands, hiddenBrandIds } = state.currentConfig;
      const index = hiddenBrandIds.indexOf(action.payload);
      const isCurrentlyHidden = index !== -1;
      if (!isCurrentlyHidden) {
        // hiddenBrandIds can carry stale ids (a brand hidden, then later
        // removed from the brand selection) — count against current brands
        // rather than hiddenBrandIds.length so a stale entry never makes
        // this guard under-count how many are actually still visible.
        const visibleCount = brands.filter((b) => !hiddenBrandIds.includes(b.id)).length;
        if (visibleCount <= 1) return;
        hiddenBrandIds.push(action.payload);
      } else {
        hiddenBrandIds.splice(index, 1);
      }
    },
    showAllBrandColumns: (state) => {
      state.currentConfig.hiddenBrandIds = [];
    },
    /** Hides every brand except the first one in display order, so at
     *  least one column always stays visible. */
    hideAllBrandColumns: (state) => {
      const order = getOrderedBrands(
        state.currentConfig.brands,
        state.currentConfig.columnOrder,
      );
      state.currentConfig.hiddenBrandIds = order.slice(1).map((b) => b.id);
    },
    updateMetricColumnHeader: (state, action: PayloadAction<string>) => {
      state.currentConfig.metricColumnHeader = action.payload;
    },
    updatePrimaryBrandHighlightColor: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.currentConfig.primaryBrandHighlightColor = action.payload;
    },
    addQuestionRow: (state, action: PayloadAction<ScorecardQuestionRow>) => {
      state.currentConfig.rows.push(action.payload);
    },
    updateQuestionRow: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<ScorecardQuestionRow> }>,
    ) => {
      const { id, changes } = action.payload;
      const row = state.currentConfig.rows.find(
        (r): r is ScorecardQuestionRow => r.type === "question" && r.id === id,
      );
      if (!row) return;
      Object.assign(row, changes);

      // Keep every dependent row's cached Base label in sync — display-only,
      // refreshed whenever the referenced row's own label changes.
      if (changes.label !== undefined) {
        findDependentRows(state.currentConfig.rows, id).forEach((dep) => {
          if (dep.rowBase?.kind === "existing_question_row") {
            dep.rowBase.questionRowLabel = row.label;
          }
        });
      }

      // A row that stops being a Percentage Question Row (its source
      // question was edited to a Number/Float question) can no longer
      // supply a Scorecard Row Base — force-clear every dependent, same as deletion.
      if (changes.resultType !== undefined && changes.resultType !== "percentage") {
        findDependentRows(state.currentConfig.rows, id).forEach((dep) => {
          dep.rowBase = null;
          dep.rowBaseNeedsAttention = true;
        });
      }
    },
    duplicateQuestionRow: (state, action: PayloadAction<string>) => {
      const index = state.currentConfig.rows.findIndex(
        (r) => r.type === "question" && r.id === action.payload,
      );
      if (index === -1) return;
      const source = state.currentConfig.rows[index] as ScorecardQuestionRow;
      state.currentConfig.rows.splice(index + 1, 0, cloneQuestionRowForDuplicate(source));
    },
    removeQuestionRow: (state, action: PayloadAction<string>) => {
      const deletedId = action.payload;
      // Any row using this one as its Scorecard Row Base loses that
      // reference and needs researcher attention before it can be saved again.
      findDependentRows(state.currentConfig.rows, deletedId).forEach((dep) => {
        dep.rowBase = null;
        dep.rowBaseNeedsAttention = true;
      });
      state.currentConfig.rows = state.currentConfig.rows.filter(
        (r) => !(r.type === "question" && r.id === deletedId),
      );
    },
    addDatasetMetricRow: (state, action: PayloadAction<ScorecardDatasetMetricRow>) => {
      state.currentConfig.rows.push(action.payload);
    },
    updateDatasetMetricRow: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<ScorecardDatasetMetricRow> }>,
    ) => {
      const { id, changes } = action.payload;
      const row = state.currentConfig.rows.find(
        (r): r is ScorecardDatasetMetricRow => r.type === "metric" && r.id === id,
      );
      if (!row) return;
      Object.assign(row, changes);
    },
    duplicateDatasetMetricRow: (state, action: PayloadAction<string>) => {
      const index = state.currentConfig.rows.findIndex(
        (r) => r.type === "metric" && r.id === action.payload,
      );
      if (index === -1) return;
      const source = state.currentConfig.rows[index] as ScorecardDatasetMetricRow;
      const duplicate: ScorecardDatasetMetricRow = {
        ...source,
        id: crypto.randomUUID(),
        label: `${source.label} (Copy)`,
        brandMappings: source.brandMappings.map((m) => ({ ...m })),
        responseCategories: source.responseCategories?.map((c) => ({ ...c })),
      };
      state.currentConfig.rows.splice(index + 1, 0, duplicate);
    },
    removeDatasetMetricRow: (state, action: PayloadAction<string>) => {
      state.currentConfig.rows = state.currentConfig.rows.filter(
        (r) => !(r.type === "metric" && r.id === action.payload),
      );
    },
    addHeadingRow: (state, action: PayloadAction<ScorecardHeadingRow>) => {
      state.currentConfig.rows.push(action.payload);
    },
    updateHeadingRow: (
      state,
      action: PayloadAction<{ id: string; label: string }>,
    ) => {
      const row = state.currentConfig.rows.find(
        (r): r is ScorecardHeadingRow =>
          r.type === "heading" && r.id === action.payload.id,
      );
      if (row) row.label = action.payload.label;
    },
    removeHeadingRow: (state, action: PayloadAction<string>) => {
      state.currentConfig.rows = state.currentConfig.rows.filter(
        (r) => !(r.type === "heading" && r.id === action.payload),
      );
    },
    reorderRows: (state, action: PayloadAction<string[]>) => {
      const byId = new Map<string, ScorecardRow>(
        state.currentConfig.rows.map((r) => [r.id, r]),
      );
      state.currentConfig.rows = action.payload
        .map((id) => byId.get(id))
        .filter((r): r is ScorecardRow => !!r);
    },
    saveCurrentConfig: (state) => {
      state.savedConfigs.push({ ...state.currentConfig });
    },
    loadConfig: (state, action: PayloadAction<ScorecardConfig>) => {
      state.currentConfig = {
        ...defaultScorecardConfig,
        ...action.payload,
        rows: (action.payload.rows ?? []).map(normalizeScorecardRow),
      };
    },
    resetToDefault: (state) => {
      state.currentConfig = { ...defaultScorecardConfig, rows: [] };
      state.datasetId = null;
      state.datasetName = null;
      state.datasetVersionId = null;
    },
  },
});

export const {
  updateChartMetadata,
  updateChartTitle,
  updateChartSubtitle,
  updateWeight,
  updatePercentageFormatting,
  updateMeanFormatting,
  updateTimePeriod,
  updateTimePeriodDates,
  updateRollingAverageEnabled,
  updateRollingAverageWindowSize,
  updateViewerOptions,
  setSourceBrandQuestion,
  setPrimaryBrand,
  setCompetitorBrandSourceIds,
  reorderBrandColumns,
  resetBrandColumnOrder,
  toggleBrandColumnVisibility,
  showAllBrandColumns,
  hideAllBrandColumns,
  updateMetricColumnHeader,
  updatePrimaryBrandHighlightColor,
  addQuestionRow,
  updateQuestionRow,
  duplicateQuestionRow,
  removeQuestionRow,
  addDatasetMetricRow,
  updateDatasetMetricRow,
  duplicateDatasetMetricRow,
  removeDatasetMetricRow,
  addHeadingRow,
  updateHeadingRow,
  removeHeadingRow,
  reorderRows,
  saveCurrentConfig,
  loadConfig,
  resetToDefault,
} = scorecardConfigSlice.actions;

export const scorecardConfigDispatcher = createDispatchers(
  scorecardConfigSlice.actions,
);
export const scorecardConfigSelector = (state: RootState) =>
  state.scorecardConfig;

export const scorecardConfigReducer = scorecardConfigSlice.reducer;
